import { Alert, Box, FormHelperText, Stack, Typography } from "@mui/material";
import { HazardCategoryPicker } from "components/Forms/HazardCategoryPicker";
import { stateNameList } from "encamp-shared/src/hazards/hazardCategoryApplicability";
import { HazardReviewReason, HazardType, Issue } from "generated-graphql/graphql";
import { useValidatingFormContext } from "hooks/useValidatingForm";
import { Control, useWatch } from "react-hook-form";

import { ChemicalDetailInputs } from ".";
import { LegacyHazardSeed, reportedLegacyHazardLabels } from "./hazardCategoryFormValues";
import { HazardReviewAlert } from "./HazardReviewAlert";

/** The compliance engine reports hazard-category issues on this key. */
const HAZARD_CATEGORY_ISSUE_KEY = "hazards";

export function HazardCategorySection({
  control,
  issues = [],
  hazardCategoryJurisdictions = [],
  chemical,
  hazardReviewReasons = [],
}: {
  control: Control<ChemicalDetailInputs>;
  issues?: readonly Issue[];
  /** States that receive the hazard categories; the rest get the legacy hazards. */
  hazardCategoryJurisdictions?: readonly string[];
  /** Seeded form values, for the legacy hazards stored before any categories. */
  chemical?: LegacyHazardSeed;
  hazardReviewReasons?: readonly HazardReviewReason[];
}) {
  const hazardIssues = issues.filter((issue) => issue.key === HAZARD_CATEGORY_ISSUE_KEY);
  const [physicalCodes, healthCodes] = useWatch({
    control,
    name: ["physicalHazardCategoryCodes", "healthHazardCategoryCodes"],
  });
  const legacy = reportedLegacyHazardLabels(
    [...(physicalCodes ?? []), ...(healthCodes ?? [])],
    chemical
  );
  const reportedIn = hazardCategoryJurisdictions.length
    ? stateNameList(hazardCategoryJurisdictions)
    : "the jurisdictions that require them";
  const isDisplayOnly = useValidatingFormContext<ChemicalDetailInputs>()?.metadata?.isDisplayOnly;

  return (
    <Stack>
      {!isDisplayOnly && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select every hazard category listed in Section 2 of the SDS. Each class takes one category
          (Category 1 is the most severe). If you pick a category that can't apply alongside one
          you've already chosen, the earlier selection is removed.
        </Typography>
      )}
      <Stack spacing={2} direction={{ xs: "column", md: "row" }}>
        <HazardCategoryPicker
          name="physicalHazardCategoryCodes"
          control={control}
          hazardType={HazardType.Physical}
          label="Physical Hazards"
        />
        <HazardCategoryPicker
          name="healthHazardCategoryCodes"
          control={control}
          hazardType={HazardType.Health}
          label="Health Hazards"
        />
      </Stack>
      {hazardIssues.map((issue, index) => (
        <FormHelperText key={`${issue.key}-${index}`} error sx={{ mx: 1.75 }}>
          {issue.message}
        </FormHelperText>
      ))}
      <Stack spacing={2} direction={{ xs: "column", md: "row" }} sx={{ mt: 2 }}>
        <HazardReviewAlert reasons={hazardReviewReasons} sx={{ flex: 1 }} />
        <Alert severity="info" role="status" sx={{ flex: 1 }}>
          <Typography variant="body2">
            The hazards above are reported in {reportedIn}. Other jurisdictions receive these
            hazards instead:
          </Typography>
          <Box component="ul" sx={{ my: 0.5, pl: 2.5 }}>
            <li>
              <Typography variant="body2" component="span" fontWeight={600}>
                Physical:{" "}
              </Typography>
              <Typography variant="body2" component="span">
                {legacy.physical.join(", ") || "None"}
              </Typography>
            </li>
            <li>
              <Typography variant="body2" component="span" fontWeight={600}>
                Health:{" "}
              </Typography>
              <Typography variant="body2" component="span">
                {legacy.health.join(", ") || "None"}
              </Typography>
            </li>
          </Box>
        </Alert>
      </Stack>
    </Stack>
  );
}
